import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck,
    faXmark,
    faTicket,
    faCalendar,
    faPlus,
    faToggleOn,
    faHashtag,
    faTag,
    faEye,
} from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../general/form/CustomForm";
import PaginatorComponent from "../general/auxiliar/ui/PaginatorComponent";
import Collapse from "../general/auxiliar/collapse/Collapse";
import EmptyTable from "../general/auxiliar/table/EmptyTable";
import LoadingTable from "../general/auxiliar/table/LoadingTable";
import OverviewTable from "../general/auxiliar/table/OverviewTable";
import { OverviewCardList } from "../general/auxiliar/table/OverviewCard";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import usePaginationFilterEffects from "../../auxiliar/customHooks/usePaginationFilterEffects";
import { handleSubmit, getTableState } from "../../auxiliar/auxFunctions";
import { getAllAcquiredVoucher } from "../../actions/acquiredVoucher";
import { isDefined } from "../../auxiliar/formatValidators";

const VoucherCardList = ({ vouchers, navigate, patientId }) => {
    const renderTitle = (voucher) => voucher.name || voucher.session_type;

    const renderSubtitle = (voucher) => (
        <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1 text-base-content/70">
                <FontAwesomeIcon
                    icon={faTag}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm truncate">{voucher.session_type}</span>
            </div>
        </div>
    );

    const renderFooter = (voucher) => (
        <div className="space-y-2 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faTicket}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">
                    {isDefined(voucher.remaining_sessions)
                        ? `${voucher.remaining_sessions} sesiones`
                        : "Ilimitadas"}
                </span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faCalendar}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">
                    {voucher.start_date.substring(0, 10)} / {voucher.due_date.substring(0, 10)}
                </span>
            </div>
            <div className="flex items-center gap-2 text-base-content/70">
                <FontAwesomeIcon
                    icon={faToggleOn}
                    className="w-3 h-3 text-base-content/40 flex-shrink-0"
                />
                <span className="truncate">{voucher.auto_assign ? "Automático" : "Manual"}</span>
            </div>
        </div>
    );

    const renderActions = (voucher, voucherId) => (
        <button
            type="button"
            className="btn btn-primary btn-xs sm:btn-sm text-xs sm:text-sm"
            onClick={handleSubmit(navigate, `/acquired-voucher/${voucherId}?patient=${patientId}`)}
        >
            <FontAwesomeIcon icon={faEye} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
            <span className="sm:hidden">Ver</span>
            <span className="hidden sm:inline">Detalles</span>
        </button>
    );

    return (
        <OverviewCardList
            data={vouchers}
            renderTitle={renderTitle}
            renderSubtitle={renderSubtitle}
            renderFooter={renderFooter}
            renderActions={renderActions}
        />
    );
};

const VoucherTable = ({ vouchers, navigate, patientId }) => {
    const columns = [
        {
            label: "Nombre",
            icon: faTicket,
            width: "w-[30%]",
        },
        {
            label: "Sesiones",
            icon: faHashtag,
            width: "w-[14%]",
        },
        {
            label: "Inicio",
            icon: faCalendar,
            width: "w-[14%]",
        },
        {
            label: "Caducidad",
            icon: faCalendar,
            width: "w-[14%]",
        },
        {
            label: "Automático",
            icon: faToggleOn,
            cellClass: "hidden xl:table-cell",
            width: "w-[14%]",
        },
    ];

    const getRowData = (voucher, voucherId) => [
        <div
            key={`${voucherId}-name`}
            className="font-medium text-base-content truncate"
            title={voucher.name}
        >
            {voucher.name}
        </div>,
        <div key={`${voucherId}-sessions`} className="text-base-content/70">
            {isDefined(voucher.remaining_sessions) ? voucher.remaining_sessions : "Ilimitadas"}
        </div>,
        <div key={`${voucherId}-start-date`} className="text-base-content/70">
            {voucher.start_date.substring(0, 10)}
        </div>,
        <div key={`${voucherId}-due-date`} className="text-base-content/70">
            {voucher.due_date.substring(0, 10)}
        </div>,
        <div key={`${voucherId}-auto-assign`} className="flex items-center">
            <FontAwesomeIcon
                icon={voucher.auto_assign ? faCheck : faXmark}
                className={`text-lg ${voucher.auto_assign ? "text-success" : "text-error"}`}
            />
        </div>,
    ];

    const getDetailPath = (voucherId) => `/acquired-voucher/${voucherId}?patient=${patientId}`;

    return (
        <OverviewTable
            data={vouchers}
            navigate={navigate}
            columns={columns}
            getRowData={getRowData}
            getDetailPath={getDetailPath}
            actionsWidth="w-[14%]"
        />
    );
};

const OverviewAcquiredVoucherInPatient = () => {
    const { register, watch, errors } = useFormWithDisable();
    const writtenFilter = {
        remaining_sessions: watch("remaining_sessions") || false,
        not_expired: watch("not_expired") || false,
    };

    const navigate = useNavigate();
    const { patient_id: patientId } = useParams();
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);
    const [vouchers, setVouchers] = useState({ waiting: true });
    const [filter, setFilter] = useState({
        per_page: 10,
        page: 1,
        clinic_id: activeClinicId,
        patient_id: patientId,
        order: "remaining_session-due_date",
    });

    const disabled = usePaginationFilterEffects(
        filter,
        writtenFilter,
        getAllAcquiredVoucher,
        setVouchers,
        setFilter,
    );

    const formConfig = {
        remaining_sessions: {
            condition: true,
            disabled: false,
            label: "Sesiones restantes",
            info: "Márcalo para mostrar solo los bonos con sesiones restantes",
            type: "checkbox",
            name: "remaining_sessions",
        },
        not_expired: {
            condition: true,
            disabled: false,
            label: "No caducados",
            info: "Márcalo para mostrar solo los bonos no caducados",
            type: "checkbox",
            name: "not_expired",
        },
    };

    const tableState = getTableState(vouchers);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando bonos..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faTicket}
                        title="No hay bonos adquiridos"
                        description="Añade el primer bono para comenzar a gestionar los bonos del paciente."
                        buttonText="Nuevo Bono"
                        onButtonClick={handleSubmit(
                            navigate,
                            `/acquired-voucher/register?patient=${patientId}`,
                        )}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <VoucherCardList
                            vouchers={vouchers}
                            navigate={navigate}
                            patientId={patientId}
                        />
                        <VoucherTable
                            vouchers={vouchers}
                            navigate={navigate}
                            patientId={patientId}
                        />
                    </>
                );
        }
    };

    return (
        <section>
            <h2 className="sr-only">
                Detalles de los bonos adquiridos de tu paciente en MiFisio Agenda
            </h2>

            <Collapse isMainTitle={true} title="Bonos Adquiridos" faModality="filter">
                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                    />
                </form>
            </Collapse>

            <div className="my-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                {/* Action button */}
                <button
                    className="btn btn-primary btn-sm w-full sm:w-auto"
                    onClick={handleSubmit(
                        navigate,
                        `/acquired-voucher/register?patient=${patientId}`,
                    )}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Nuevo Bono
                </button>

                {/* Pagination */}
                <div className="w-full sm:w-auto">
                    <PaginatorComponent
                        pagination={vouchers.pagination}
                        setFilter={setFilter}
                        filter={filter}
                        disabled={disabled}
                    />
                </div>
            </div>

            {renderContent()}

            {/* Pagination for small screens - duplicated to improve UX */}
            <div className="w-full my-5 sm:w-auto lg:hidden">
                <PaginatorComponent
                    pagination={vouchers.pagination}
                    setFilter={setFilter}
                    filter={filter}
                    disabled={disabled}
                    showTotalItems={false}
                />
            </div>
        </section>
    );
};

export default OverviewAcquiredVoucherInPatient;
